import React from 'react';
import { TablePagination, Box, Pagination } from '@mui/material';
import { PaginationState } from '../../types';

export interface EnhancedTablePaginationProps {
  pagination: PaginationState;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
}

export function EnhancedTablePagination({
  pagination,
  onPageChange,
  onRowsPerPageChange,
}: EnhancedTablePaginationProps) {
  if (!pagination) return null;

  // Table variant pagination
  if (pagination.variant === 'table') {
    return (
      <TablePagination
        count={pagination.count}
        rowsPerPage={pagination.rowsPerPage}
        page={pagination.page}
        onPageChange={(event, newPage) => {
          // Update internal table state
          onPageChange(event, newPage);
          // Also notify external pagination state if provided (e.g., stories)
          pagination.onPageChange?.(event, newPage);
        }}
        onRowsPerPageChange={
          ((
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            onRowsPerPageChange(event);
            pagination.onRowsPerPageChange?.(event);
          }) as (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => void
        }
        rowsPerPageOptions={pagination.rowsPerPageOptions}
      />
    );
  }

  // Numbered variant pagination
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>Rows per page:</span>
          <select
            value={pagination.rowsPerPage}
            onChange={
              onRowsPerPageChange as (
                event: React.ChangeEvent<HTMLSelectElement>,
              ) => void
            }
            style={{
              padding: '4px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          >
            {(pagination.rowsPerPageOptions || [5, 10, 25]).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </Box>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {`${pagination.page * pagination.rowsPerPage + 1}-${Math.min(
            (pagination.page + 1) * pagination.rowsPerPage,
            pagination.count,
          )} of ${pagination.count}`}
        </Box>
      </Box>
      <Pagination
        count={Math.ceil(pagination.count / pagination.rowsPerPage)}
        page={pagination.page + 1}
        onChange={(event, page) => {
          onPageChange(event, page - 1);
          pagination.onPageChange?.(event, page - 1);
        }}
        variant="outlined"
        shape="rounded"
      />
    </Box>
  );
}
